import { Heading } from "../../components/Heading/Heading";
import { DotButton } from "../../components/EmblaCarouselControls/EmblaCarouselControls";
import { Section } from "../../components/Section/Section";
import styled, { css } from "styled-components";
import { Subheading } from "../../components/Subheading/Subheading";

interface DynamicFormSection extends Section {
  backgroundType?: string;
  backgroundColor?: string;
}

interface DynamicFormContainer {
  align?: "start" | "end" | "center";
}

interface Content {
  align?: "start" | "end" | "center";
}

interface ContentTitle {
  color?: string;
  align?: "start" | "end" | "center";
}

interface ContentVideo {
  align?: "start" | "end" | "center";
}

interface ContentDescription {
  color?: string;
}

export const textParagraphDefaultRegular = css`
  font-size: 1rem;
  line-height: 150%;
  font-weight: 500;
`;

export const textParagraphXsRegular = css`
  font-size: 0.75rem;
  line-height: 150%;
  font-weight: 400;
`;

export const DynamicFormSection = styled(
  Section
)<DynamicFormSection>`
  position: relative;
  width: 100%;

  background-color: ${({ backgroundType, backgroundColor }) =>
    backgroundType !== "image" ? backgroundColor || "var(--general-background, #F1F5F9)" : ""};
  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
`;

export const DynamicFormBackground = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  .image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .desktop-image {
    display: block;
    
    @media (max-width: 767px) {
      display: none;
    }
  }

  .mobile-image {
    display: none;
    
    @media (max-width: 767px) {
      display: block;
    }
  }
`;

export const DynamicFormContainer = styled.div<DynamicFormContainer>`
  position: relative;
  z-index: 1;
  max-width: 1216px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 768px) {
    display: flex;
    gap: 2rem;
  }

  ${({ align }) =>
    align === "center"
      ? css`
          flex-direction: column;
        `
      : css`
          flex-direction: row;
        `}

  ${({ align }) =>
    align == "end" &&
    css`
      flex-direction: row-reverse;
    `}
`;

export const FormContainer = styled.div`
  width: 100%;
  border-radius: var(--design-card-border-radius, 8px);
  background-color: ${props => props.color || "var(--cards-background, #ffffff)"};

  @media (min-width: 768px) {
    max-width: 490px;
  }

  @media (max-width: 767px) {
    margin-top: 1.5rem;
  }
`;

export const FormTitle = styled.h3`
  ${textParagraphDefaultRegular}
  width: 100%;
  color: ${props => props.color || "var(--text-title, #0f172a)"};
  padding: 1rem;
  text-align: center;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
  text-align: left;
`;

export const FormSpanError = styled.span`
  ${textParagraphXsRegular}
  color: #F3322B;
`;

export const Content = styled.div<Content>`
  width: 100%;
  z-index: 50;

  ${({ align }) =>
    align != "center" &&
    css`
      @media (min-width: 768px) {
        position: sticky;
      }
    `}

  @media (min-width: 768px) {
    top: 0;
    height: 100%;
  }

  @media (max-width: 767px) {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }
`;

export const ContentTitle = styled(Heading)<ContentTitle>`
  width: 100%;
  margin-bottom: 1rem;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
  color: ${props => props.color || "var(--text-title, #0f172a)"};

  ${({ align }) =>
    align === "center"
      ? css`
          text-align: center;
        `
      : css`
          text-align: left;
        `}

  @media (max-width: 767px) {
    text-align: center;
  }
`;

export const ContentDescription = styled(Subheading)<ContentDescription>`
  margin-left: auto;
  margin-right: auto;
  max-width: 51rem;
  text-align: left;
  word-break: normal;
  overflow-wrap: break-word;
  hyphens: none;
  color: ${props => props.color || "var(--text-body, #475569)"};

  @media (min-width: 768px) {
    margin-bottom: 3.5rem;
  }

  @media (max-width: 767px) {
    margin-bottom: 1.5rem;
    text-align: center;
  }
`;

export const ContentImage = styled.div`
  .image {
    width: 100%;
    object-fit: cover;
  }

  .desktop-image {
    display: block;
    
    @media (max-width: 767px) {
      display: none;
    }
  }

  .mobile-image {
    display: none;
    
    @media (max-width: 767px) {
      display: block;
    }
  }
`;

export const ContentVideo = styled.div<ContentVideo>`
  width: 100%;
  max-width: 690px;
  display: flex;

  ${({ align }) =>
    align == "center" &&
    css`
      justify-content: center;
    `}
`;

export const ContentVideoArea = styled.div`
  position: relative;
  width: 100%;
  padding-top: 57.97%;

  .iframe-area {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
  }
`;

export const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const FieldTitle = styled.p`
  font-size: 1rem;
  line-height: 150%;
  font-weight: 600;
  color: ${props => props.color || "var(--text-title, #0f172a)"};
`;
export const FieldWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

export const FieldWrapperColumn = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 1.875rem;
  row-gap: 0.5rem;
`;

export const FieldSeparator = styled.div`
  width: 100%;
  height: 0.0625rem;
  background-color: var(--general-border, #080404);
  opacity: 0.6;
`;

export const FieldLabel = styled.label`
  display: flex;
  gap: 0.5rem;
  color: var(--text-body, #475569);

  ${textParagraphDefaultRegular}

  input[type="checkbox"] {
    accent-color: var(--general-background, #3754e2);
  }

  input[type="radio"] {
    accent-color: var(--general-background, #3754e2);
  }
`;

export const FieldLabelXs = styled.label`
  display: flex;
  gap: 0.5rem;

  ${textParagraphXsRegular}

  input[type="checkbox"] {
    accent-color: var(--general-background, #3754e2);
  }
`;

export const PrivacyPolicy = styled.div`
  color: ${props => props.color || "var(--text-body, #475569)"};
  font-size: 0.875rem;
  line-height: 150%;
  font-weight: 400;
  margin-top: -0.1875rem;

  a {
    color: inherit;
    text-decoration: underline;

    &:hover {
      opacity: 0.8;
    }
  }
`;

export const FormMessage = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (min-width: 768px) {
    height: 39.0625rem;
  }

  @media (max-width: 767px) {
    height: 20.3125rem;
  }
`;

export const FormMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (min-width: 768px) {
    max-width: 20.0625rem;
  }
`;

export const FormMessageTitle = styled.p`
  text-align: center;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 130%;
`;

export const Carousel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const CarouselWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
`;

export const CarouselContainer = styled.div`
  position: relative;
  max-width: 696px;
`;

export const CarouselContainerInner = styled.div`
  margin-left: -2rem;

  @media (max-width: 767px) {
    margin-left: -1rem;
  }
`;

export const CarouselSlide = styled.div`
  flex-shrink: 0;
  flex-grow: 0;
  padding-left: 2rem;
  flex-basis: 100%;

  @media (max-width: 767px) {
    flex-basis: 100%;
    padding-left: 1rem;
  }
`;

export const CarouselPrev = styled.div`
  position: absolute;
  left: 16px;
  z-index: 50;

  .forcePosition {
    position: relative !important;
  }
`;

export const CarouselNext = styled.div`
  position: absolute;
  right: 16px;
  z-index: 50;

  .forcePosition {
    position: relative !important;
  }
`;

export const CarouselImage = styled.div`
  .image {
    width: 100%;
    object-fit: cover;
  }

  .desktop-image {
    display: block;
    
    @media (max-width: 767px) {
      display: none;
    }
  }

  .mobile-image {
    display: none;
    
    @media (max-width: 767px) {
      display: block;
    }
  }
`;

export const CarouselScroll = styled.div`
  position: absolute;
  bottom: 16px;
  width: 100%;
  place-content: center !important;
`;

export const CarouselDot = styled(DotButton).withConfig({
  shouldForwardProp: (prop) => prop !== 'active'
})<{ active?: boolean }>`
  width: 12px;
  height: 12px;
  ${({ active }) =>
    active
      ? css`
          opacity: 1;
        `
      : css`
          opacity: 0.2;
        `}
`;

export const FormSeparator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #CBD5E1;
`;

export const HighlightBox = styled.div<{ $bgColor?: string }>`
  display: flex;
  width: 100%;
  max-width: 490px;
  padding: var(--spacing-24, 24px);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-2, 8px);
  border-radius: var(--button-radius-Arredondado, 12px);
  border: var(--border-with-fina-default, 1px) solid var(--general-border, #E2E8F0);
  background: ${props => props.$bgColor || 'var(--cards-background, #FFF)'};
`;

export const HighlightPreTitle = styled.span<{ $color?: string }>`
  color: ${props => props.$color || 'var(--text-body, #475569)'};
  font-size: var(--sizes-descries, 20px);
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

export const HighlightTitle = styled.span<{ $color?: string }>`
  color: ${props => props.$color || 'var(--general-primary, #3754E2)'};
  font-size: var(--sizes-ttulos, 40px);
  font-style: normal;
  font-weight: 600;
  line-height: 120%;
`;

interface FormContentWrapper {
  align?: "start" | "end" | "center";
}

export const FormContentWrapper = styled.div<FormContentWrapper>`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  max-width: 490px;
  
  ${({ align }) =>
    align === "center" &&
    css`
      align-self: center;
    `}
`;